'use client';

import { useEffect, useState } from 'react';

export type Status = 'idle' | 'loading' | 'ready' | 'error';

function createScript(src: string, testId?: string) {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.setAttribute('data-status', 'loading');
    // Add script to document body
    document.body.appendChild(script);

    if (testId) {
        script.setAttribute('data-testid', testId);
    }

    return script;
}

export type ScriptElt = HTMLScriptElement | null;

/**
 * Adapted from https://usehooks-typescript.com/react-hook/use-script
 */
export function useScript(src: string, testId?: string): Status {
    const [status, setStatus] = useState<Status>(src ? 'loading' : 'idle');

    useEffect(() => {
        if (!src) {
            setStatus('idle');
            return undefined;
        }

        // Fetch existing script element by src
        // It may have been added by another instance of this hook
        let script: ScriptElt = document.querySelector(`script[src="${src}"]`);

        if (!script) {
            script = createScript(src, testId);
        } else {
            // Grab existing script status from attribute and set to state.
            setStatus(script.getAttribute('data-status') as Status);
        }

        // Grab existing script status from attribute and set to state.
        const currentStatus = script.getAttribute('data-status') as Status;

        if (currentStatus === 'ready') {
            // Script already loaded, but need to verify grecaptcha.enterprise availability
            waitForGrecaptcha(setStatus);
        } else if (currentStatus === 'error') {
            setStatus('error');
        } else {
            // Add event listeners for load/error to update status
            const onScriptLoad = () => {
                script?.setAttribute('data-status', 'ready');
                // After load, confirm grecaptcha.enterprise is ready
                waitForGrecaptcha(setStatus);
            };

            const onScriptError = () => {
                script?.setAttribute('data-status', 'error');
                setStatus('error');
            };

            script.addEventListener('load', onScriptLoad);
            script.addEventListener('error', onScriptError);

            // Remove event listeners on cleanup
            return () => {
                script?.removeEventListener('load', onScriptLoad);
                script?.removeEventListener('error', onScriptError);
            };
        }
    }, [src, testId]);

    return status;
}

/**
 * Waits until window.grecaptcha.enterprise.execute is available,
 * polling every 50ms for up to ~5 seconds.
 * Once available, sets status to 'ready'.
 * If timeout reached, sets status to 'error'.
 */
function waitForGrecaptcha(setStatus: (status: Status) => void) {
    const maxAttempts = 100;
    let attempts = 0;

    const interval = setInterval(() => {
        const grecaptchaAvailable = typeof window !== 'undefined' && window.grecaptcha;
        const enterpriseAvailable = grecaptchaAvailable && window?.grecaptcha?.enterprise;
        const executeAvailable = enterpriseAvailable && window?.grecaptcha?.enterprise.execute;

        if (executeAvailable) {
            clearInterval(interval);
            setStatus('ready');
        } else if (++attempts > maxAttempts) {
            clearInterval(interval);
            setStatus('error');
        }
    }, 50);
}
